import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '@/lib/api';
import { Upload, FolderPlus, Pencil } from 'lucide-react';
import { useConfirmation } from '../components/shared/ZCard';
import OverflowMenu from '@/components/shared/OverflowMenu';
import { DeleteConfirmDialog } from '@/components/DeleteConfirmDialog';
import ZTabs from '@/components/shared/ZTabs';
import ZEmptyState from '@/components/shared/ZEmptyState';
import Logotype from '@/components/shared/Logotype';
import CompositionTool from '@/components/shared/CompositionTool';

// Import extracted components
import {
  SortablePhotoGrid,
  PhotoViewer,
  UploadDialog,
  EditDialog,
  CollectionForm,
  CollectionDetail,
  FlowEditDialog,
} from '@/components/framed';

// ==================== MAIN FRAMED PAGE ====================
export default function Framed() {
  const navigate = useNavigate();
  const feedback = useConfirmation();
  const [photos, setPhotos] = useState([]);
  const [curated, setCurated] = useState([]);
  const [activeColl, setActiveColl] = useState(null);
  const [viewerIndex, setViewerIndex] = useState(-1);
  const [viewerPhotos, setViewerPhotos] = useState([]);
  const [showUpload, setShowUpload] = useState(false);
  const [editingPhoto, setEditingPhoto] = useState(null);
  const [showCollForm, setShowCollForm] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState({ open: false, type: '', id: null, name: '' });
  const [flowSettings, setFlowSettings] = useState(null);
  const [showFlowEdit, setShowFlowEdit] = useState(false);
  const [loadingCollection, setLoadingCollection] = useState(false);
  const [composingPhoto, setComposingPhoto] = useState(null);

  useEffect(() => { loadAll(); }, []);

  useEffect(() => {
    const handler = (e) => { if (e.detail?.app === 'framed') loadAll(); };
    window.addEventListener('universal-input-applied', handler);
    return () => window.removeEventListener('universal-input-applied', handler);
  }, []);

  const loadAll = async () => {
    try {
      const [photosRes, curatedRes, flowRes] = await Promise.all([
        api.get('/photos'), 
        api.get('/framed/curated'),
        api.get('/framed/flow-settings')
      ]);
      setPhotos(photosRes.data);
      setCurated(curatedRes.data);
      setFlowSettings(flowRes.data);
    } catch {}
  };

  const openCollection = async (coll) => {
    setLoadingCollection(true);
    setActiveColl({ ...coll, photos: [] });
    try {
      const { data } = await api.get(`/framed/curated/${coll.id}`);
      setActiveColl(data);
    } catch {}
    finally { setLoadingCollection(false); }
  };

  const deletePhoto = async (id) => {
    try {
      await api.delete(`/photos/${id}`);
      setPhotos(prev => prev.filter(p => p.id !== id));
      feedback.confirm();
    } catch { feedback.fail(); }
  };

  const confirmDeletePhoto = (photo) => {
    setDeleteConfirm({ open: true, type: 'photo', id: photo.id, name: photo.tagline || photo.title || 'this photo' });
  };

  const handleDeleteConfirm = () => {
    if (deleteConfirm.type === 'photo') {
      deletePhoto(deleteConfirm.id);
      setViewerIndex(-1);
    }
  };

  const onNavigate = useCallback((i) => setViewerIndex(i), []);

  const handlePostcard = (photo) => {
    setViewerIndex(-1);
    setComposingPhoto(photo);
  };

  const toggleFlowPublic = async () => {
    const newVal = !flowSettings?.is_public;
    setFlowSettings(prev => ({ ...prev, is_public: newVal }));
    try {
      await api.put('/framed/flow-settings', { is_public: newVal });
    } catch {
      setFlowSettings(prev => ({ ...prev, is_public: !newVal }));
      feedback.fail();
    }
  };

  const openViewer = (photoList, idx) => {
    setViewerPhotos(photoList);
    setViewerIndex(idx);
  };

  const togglePublic = async (photo) => {
    const newVal = !photo.is_public;
    // Optimistic update in local state
    setPhotos(prev => prev.map(p => p.id === photo.id ? { ...p, is_public: newVal } : p));
    // Also update active collection photos if viewing one
    if (activeColl) {
      setActiveColl(prev => prev ? { ...prev, photos: (prev.photos || []).map(p => p.id === photo.id ? { ...p, is_public: newVal } : p) } : prev);
    }
    // Also update viewer photos
    setViewerPhotos(prev => prev.map(p => p.id === photo.id ? { ...p, is_public: newVal } : p));
    try {
      await api.put(`/photos/${photo.id}`, { is_public: newVal });
    } catch {
      // Rollback
      setPhotos(prev => prev.map(p => p.id === photo.id ? { ...p, is_public: !newVal } : p));
      if (activeColl) {
        setActiveColl(prev => prev ? { ...prev, photos: (prev.photos || []).map(p => p.id === photo.id ? { ...p, is_public: !newVal } : p) } : prev);
      }
      setViewerPhotos(prev => prev.map(p => p.id === photo.id ? { ...p, is_public: !newVal } : p));
      feedback.fail();
    }
  };

  const handleSetCover = async (photoId) => {
    if (!activeColl) return;
    try {
      await api.put(`/framed/curated/${activeColl.id}`, { cover_photo_id: photoId });
      feedback.confirm();
      openCollection(activeColl);
    } catch { feedback.fail(); }
  };

  // Shared header component
  const PageHeader = () => (
    <div className="mb-6">
      <div className="flex items-center mb-1" style={{ gap: 2 }} data-testid="framed-title">
        <Logotype app="framed" size="header" />
      </div>
      <p className="text-xs tracking-[0.2em] uppercase" style={{ fontFamily: "'JetBrains Mono', monospace", color: '#b0b4c0', paddingLeft: 42 }}>
        A Life in Technicolor
      </p>
    </div>
  );

  // Shared nav pane component
  const framedTabs = [
    { id: '__flow__', label: flowSettings?.name || 'Flow' },
    ...curated.map(c => ({ id: c.id, label: c.title })),
  ];

  const NavPane = () => (
    <div className="mb-6">
      <div className="flex items-center gap-2">
        <div className="flex-1 overflow-x-auto">
          <ZTabs
            tabs={framedTabs}
            activeTab={activeColl ? activeColl.id : '__flow__'}
            onChange={(id) => {
              if (id === '__flow__') { setActiveColl(null); }
              else { const coll = curated.find(c => c.id === id); if (coll) openCollection(coll); }
            }}
            accent="#6B35B8"
          />
        </div>
      </div>
    </div>
  );

  // Collection detail view
  if (activeColl) {
    return (
      <div data-testid="framed-page">
        <PageHeader />
        <NavPane />
        <CollectionDetail
          collection={activeColl}
          onBack={() => { setActiveColl(null); loadAll(); }}
          onRefresh={() => openCollection(activeColl)}
          onViewPhoto={openViewer}
          onTogglePublic={togglePublic}
          onPostcard={handlePostcard}
          onEdit={setEditingPhoto}
          onDelete={confirmDeletePhoto}
        />
        {viewerIndex >= 0 && (
          <PhotoViewer
            photos={viewerPhotos}
            index={viewerIndex}
            onClose={() => setViewerIndex(-1)}
            onNavigate={onNavigate}
            isOwner={true}
            onEdit={setEditingPhoto}
            onDelete={confirmDeletePhoto}
            onPostcard={handlePostcard}
            collectionId={activeColl.id}
            onSetCover={handleSetCover}
            currentCoverId={activeColl.cover_photo_id}
            onTogglePublic={togglePublic}
          />
        )}
        <EditDialog photo={editingPhoto} onClose={() => setEditingPhoto(null)} onSave={() => { loadAll(); openCollection(activeColl); }} />

        <DeleteConfirmDialog
          open={deleteConfirm.open}
          onClose={() => setDeleteConfirm({ open: false, type: '', id: null, name: '' })}
          onConfirm={handleDeleteConfirm}
          itemName={deleteConfirm.name}
          itemType={deleteConfirm.type}
        />
        <CollectionForm open={showCollForm} onClose={() => setShowCollForm(false)} onSave={loadAll} />
        <UploadDialog open={showUpload} onClose={() => setShowUpload(false)} onSave={loadAll} />
        {composingPhoto && (
          <CompositionTool
            photo={composingPhoto}
            mode="postcard"
            onClose={() => setComposingPhoto(null)}
            onSave={() => {}}
          />
        )}
      </div>
    );
  }

  // Flow view (main photo stream)
  return (
    <div data-testid="framed-page">
      <PageHeader />
      <NavPane />

      {/* Flow Header */}
      <div className="flex items-end justify-between mb-6 flex-wrap gap-4" style={{ position: 'relative' }}>
        <div>
          <h2 className="text-2xl sm:text-3xl font-bold tracking-tight" style={{ fontFamily: "'Playfair Display', serif", color: '#f0f0f5' }}>
            {flowSettings?.name}
          </h2>
          <p className="italic mt-1" style={{ fontFamily: "'Cormorant Garamond', serif", color: '#b0b4c0', fontSize: '17px' }}>
            {flowSettings?.tagline}
          </p>
          <p className="text-xs mt-2" style={{ fontFamily: "'Cormorant Garamond', serif", color: '#6b7084' }}>
            {photos.length} {photos.length === 1 ? 'photograph' : 'photographs'}
          </p>
        </div>
        <OverflowMenu
          publishToggle={{ isPublished: flowSettings?.is_public || false, onToggle: toggleFlowPublic }}
          items={[
            { icon: <Upload size={16} strokeWidth={1.5} />, label: 'Upload photos', onClick: () => setShowUpload(true) },
            { icon: <FolderPlus size={16} strokeWidth={1.5} />, label: 'New collection', onClick: () => setShowCollForm(true) },
            { icon: <Pencil size={16} strokeWidth={1.5} />, label: 'Edit info', onClick: () => setShowFlowEdit(true) },
          ]}
        />
      </div>

      {/* Photos - Sortable Grid View */}
      <div data-testid="photo-stream">
        {photos.length === 0 ? (
          <ZEmptyState mark="F" accent="#6B35B8" message="No photographs yet." />
        ) : (
          <SortablePhotoGrid
            photos={photos}
            onReorder={(newOrder) => {
              setPhotos(newOrder);
              api.put('/framed/flow-settings', { photo_order: newOrder.map(p => p.id) })
                .then(() => feedback.confirm())
                .catch(() => {
                  feedback.fail();
                  loadAll();
                });
            }}
            onPhotoClick={openViewer}
            collection={null}
            showCoverBadge={false}
            onTogglePublic={togglePublic}
            onPostcard={handlePostcard}
            onEdit={setEditingPhoto}
            onDelete={confirmDeletePhoto}
          />
        )}
      </div>

      {/* Viewer */}
      {viewerIndex >= 0 && (
        <PhotoViewer
          photos={viewerPhotos}
          index={viewerIndex}
          onClose={() => setViewerIndex(-1)}
          onNavigate={onNavigate}
          isOwner={true}
          onEdit={setEditingPhoto}
          onDelete={confirmDeletePhoto}
          onPostcard={handlePostcard}
          onTogglePublic={togglePublic}
        />
      )}

      <UploadDialog open={showUpload} onClose={() => setShowUpload(false)} onSave={loadAll} />
      <EditDialog photo={editingPhoto} onClose={() => setEditingPhoto(null)} onSave={loadAll} />
      <CollectionForm open={showCollForm} onClose={() => setShowCollForm(false)} onSave={loadAll} />
      <FlowEditDialog open={showFlowEdit} onClose={() => setShowFlowEdit(false)} settings={flowSettings} onSave={loadAll} />
      <DeleteConfirmDialog
        open={deleteConfirm.open}
        onClose={() => setDeleteConfirm({ open: false, type: '', id: null, name: '' })}
        onConfirm={handleDeleteConfirm}
        itemName={deleteConfirm.name}
        itemType={deleteConfirm.type}
      />
      {composingPhoto && (
        <CompositionTool
          photo={composingPhoto}
          mode="postcard"
          onClose={() => setComposingPhoto(null)}
          onSave={() => {}}
        />
      )}
    </div>
  );
}
